/**
 * THE COMPACT ROW (frame 56): 64px — a 40px thumb, the name, a muted sub-line, and a right
 * column with the date and a toned state; a 3px left edge in the row's state tone (rule 6:
 * state is an edge, not a badge). One row for all four phone lists (maintainer, 2 Sep 2026
 * — "frame rows everywhere"); each screen supplies the words.
 *
 * The whole row is the door (the phone card's own rule since 18 Aug): `onClick` navigates
 * or toggles; the `lead` slot holds the select-mode checkbox, which stops propagation.
 */
import type { ReactNode } from 'react';

export type Tone = 'ok' | 'warn' | 'info' | 'inert';

export function CompactRow({
  name,
  sub,
  thumbUrl,
  noThumb = false,
  date,
  state,
  edge,
  lead,
  trailing,
  index,
  onClick,
  href,
  className,
  disabled,
  pointerHandlers,
}: {
  name: string;
  sub?: ReactNode;
  thumbUrl?: string | undefined;
  /** Projects have no photo: the row starts at the name (frame 56's shape, minus the thumb). */
  noThumb?: boolean;
  date?: ReactNode;
  state?: { text: string; tone: Tone } | undefined;
  /** The 3px edge colour — a token expression (`var(--warn-edge)`), never a literal. */
  edge: string;
  lead?: ReactNode;
  trailing?: ReactNode;
  /** Visual-order index for the selection gestures (`data-row-index`). */
  index?: number;
  onClick?: () => void;
  /** The keyboard door — an anchor over the name when given. */
  href?: string;
  className?: string;
  disabled?: boolean;
  /** Spread onto the row — the reservations list's touch long-press (0.1.523, `use-long-press.ts`). */
  pointerHandlers?: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
  };
}): JSX.Element {
  return (
    <li
      className={`gl-row${className === undefined ? '' : ` ${className}`}`}
      // THE ROW'S ONE STROKE IS ITS LEFT BORDER, coloured through `--row-edge` (0.1.527). Under a
      // group the row itself begins at the stroke — styles.css `GROUPED ROWS BEGIN AT THEIR STROKE`.
      style={{ ['--row-edge' as string]: edge }}
      data-row-index={index}
      aria-disabled={disabled === true ? true : undefined}
      onClick={onClick}
      {...pointerHandlers}
    >
      {lead}
      {!noThumb &&
        (thumbUrl !== undefined ? (
          <img className="gl-thumb" src={thumbUrl} alt="" loading="lazy" />
        ) : (
          <span className="gl-thumb" aria-hidden="true" />
        ))}
      <span className="gl-main">
        {href !== undefined ? (
          <a
            className="gl-name"
            href={href}
            // No aria-label: the link's accessible name is the NAME, the flat rows' own
            // contract — `getByRole('link', { name })` opens a garment in a dozen specs.
            onClick={(e) => {
              // The row owns navigation (and the select-mode toggle); the anchor is the
              // keyboard/middle-click door and must not double-fire.
              e.preventDefault();
            }}
          >
            {name}
          </a>
        ) : (
          <span className="gl-name">{name}</span>
        )}
        {sub !== undefined && <span className="gl-sub">{sub}</span>}
      </span>
      {(date !== undefined || state !== undefined) && (
        <span className="gl-side">
          {date !== undefined && <span className="gl-date">{date}</span>}
          {state !== undefined && <span className={`gl-state gl-state-${state.tone}`}>{state.text}</span>}
        </span>
      )}
      {trailing}
    </li>
  );
}
